import { Injectable, signal } from '@angular/core';
import { fromEvent, Subject, takeUntil, Observable } from 'rxjs';
import { Action } from '@actions/types';

export interface ExitIntentConfig {
  sensitivity?: number;
}

export interface ExitIntentOutput {
  onExitIntent$: Observable<void>;
  startListening: () => void;
  stopListening: () => void;
  reset: () => void;
  hasBeenTriggered: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ExitIntentAction implements Action<ExitIntentConfig, ExitIntentOutput> {
  private exitIntentSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private isListening = signal(false);
  private hasTriggered = signal(false);

  constructor() {}

  run(config: ExitIntentConfig = {}): ExitIntentOutput {
    const { sensitivity = 10 } = config;

    return {
      onExitIntent$: this.exitIntentSubject.asObservable(),
      startListening: () => this.startListening(sensitivity),
      stopListening: () => this.stopListening(),
      reset: () => this.reset(),
      hasBeenTriggered: this.hasTriggered()
    };
  }

  private startListening(sensitivity: number): void {
    if (this.isListening() || this.hasTriggered()) {
      return;
    }

    this.isListening.set(true);

    fromEvent<MouseEvent>(document, 'mouseleave')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.clientY <= sensitivity && !this.hasTriggered()) {
          this.triggerExitIntent();
        }
      });

    // fromEvent<BeforeUnloadEvent>(window, 'beforeunload')
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(() => {
    //     if (!this.hasTriggered()) {
    //       this.triggerExitIntent();
    //     }
    //   });

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.hidden && !this.hasTriggered()) {
          this.triggerExitIntent();
        }
      });
  }

  private stopListening(): void {
    this.isListening.set(false);
    this.destroy$.next();
  }

  private reset(): void {
    this.hasTriggered.set(false);
  }

  private triggerExitIntent(): void {
    if (this.hasTriggered()) {
      return;
    }

    this.hasTriggered.set(true);
    this.exitIntentSubject.next();
  }
  
  ngOnDestroy(): void {
    this.stopListening();
    this.exitIntentSubject.complete();
  }
}
